/** @jsx jsx */

import { Fragment } from 'react';
import { jsx } from '@emotion/core';
import { Icon, Link, Text } from '@zapier/design-system';

import { getAppUrl } from 'app/developer-v3/utils';
import withTrackInteraction, {
  WithPartnerUserInteractionTrackerProps,
} from 'app/developer-v3/teams/team-partner-sharing/withPartnerUserInteractionTracker';

import { BaseTile } from './BaseTile';
import CountDisplay from '../CountDisplay';
import { useQueryIntegrationIssuesAll } from 'app/developer-v3/hooks/useQueryIssuesAll';

interface BugsAndFeatureTileProps
  extends WithPartnerUserInteractionTrackerProps {
  appId: string;
  dataTestId?: string;
}

export const BugsAndFeatureTile = ({
  appId,
  dataTestId,
  handleTrackInteraction,
}: BugsAndFeatureTileProps) => {
  const { data, isError, isLoading } = useQueryIntegrationIssuesAll(
    {
      actions: [],
      integrationId: Number(appId),
      orderBy: 'priority',
      orderDirection: 'ASC',
    },
    {
      keepPreviousData: true,
      staleTime: 60000,
    }
  );
  const issues = data || [];
  const bugs = issues.filter(({ typeOfDisplay }) => typeOfDisplay === 'Bug');
  const featureRequests = issues.filter(
    ({ typeOfDisplay }) => typeOfDisplay === 'Feature Request'
  );

  return (
    <BaseTile
      dataTestId={dataTestId}
      headerContent="Bugs & feature requests"
      icon={<Icon name="actionBug" size="24px" />}
      isError={isError}
      isLoading={isLoading}
      tipContent={
        <Fragment>
          To grow your integration and get better performance, review your{' '}
          <Link
            href={`${getAppUrl(appId)}/issues`}
            onClick={() =>
              handleTrackInteraction({
                eventAction: 'click',
                interactionGoal:
                  'Views bugs and features from Insights dashboard',
                interactionName: 'view_bugs_and_features',
              })
            }
          >
            bugs and feature requests.
          </Link>
        </Fragment>
      }
      tipContentStyle={{ minHeight: 60 }}
      toolTipContent={
        <Text color="PrimeWhite" tag="p" type="Body1">
          Total number of open bugs and feature requests for the integration.
        </Text>
      }
      tooltipPosition="southeast"
    >
      <CountDisplay count={bugs.length} label="bugs" />
      <CountDisplay count={featureRequests.length} label="feature requests" />
    </BaseTile>
  );
};

export default withTrackInteraction(BugsAndFeatureTile);
